import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const TONES = {
  royal: "#1D4ED8",
  cyan: "#06B6D4",
  aqua: "#22D3EE",
  teal: "#0EA5A4",
  orange: "#F97316",
  jungle: "#16A34A",
  gold: "#E0A82E",
  coral: "#FB6F5A",
  purple: "#7C3AED",
  magenta: "#EC4899",
};

export const StatCard = ({
  label,
  value,
  hint,
  icon: Icon,
  trend,
  trendDir = "up",
  tone,
  index = 0,
  testid,
  to,
  ariaLabel,
}) => {
  const c = TONES[tone] || "var(--accent-primary)";
  const TrendIcon = trendDir === "up" ? ArrowUpRight : ArrowDownRight;
  const clickable = !!to;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      data-testid={testid || "stat-card"}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-[#E4E9F3] bg-white p-5 pt-6 shadow-[0_2px_12px_rgba(20,36,95,0.05)] transition-all duration-200",
        clickable
          ? "cursor-pointer hover:-translate-y-1 hover:border-[#C7D2FE] hover:shadow-[0_10px_28px_rgba(20,36,95,0.12)] focus-within:ring-2 focus-within:ring-[#1D4ED8]/50 focus-within:ring-offset-2"
          : ""
      )}
    >
      {clickable && (
        <Link
          to={to}
          data-testid={testid ? `${testid}-link` : undefined}
          aria-label={ariaLabel || `View ${label}`}
          className="absolute inset-0 z-[2] rounded-2xl focus:outline-none"
        />
      )}
      {/* top colour band */}
      <span className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: c }} />
      {/* faint orbit motif */}
      <span
        className="pointer-events-none absolute -bottom-8 -right-8 h-24 w-24 rounded-full border-[10px] opacity-[0.10] transition-opacity group-hover:opacity-20"
        style={{ borderColor: c }}
      />
      <div className="relative flex items-start justify-between">
        <span className="max-w-[70%] text-[11px] font-bold uppercase leading-tight tracking-[0.13em] text-[#5A6B8C]">
          {label}
        </span>
        {Icon && (
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
            style={{ backgroundColor: c }}
          >
            <Icon size={19} strokeWidth={2.3} />
          </span>
        )}
      </div>
      <div className="relative mt-3 flex items-end justify-between">
        <span className="font-numeric text-4xl font-bold leading-none tracking-tight text-[#14245F]">
          {value}
        </span>
        {trend && (
          <span
            className={cn(
              "flex items-center gap-0.5 text-xs font-bold",
              trendDir === "up" ? "text-[#16A34A]" : "text-[#DC2626]"
            )}
          >
            <TrendIcon size={14} strokeWidth={2.5} />
            {trend}
          </span>
        )}
      </div>
      {hint && <p className="relative mt-1.5 text-xs font-medium text-[#5A6B8C]">{hint}</p>}
    </motion.div>
  );
};
